import React, { FC, useEffect, useState, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { Box, useTheme } from '@mui/material';
import { Typography } from '@mui/material';
import TradingHoursModals from './TradingHoursModals';
import { AvaSuburbHours } from '@models';
import Alert from '@mui/material/Alert';
import { useAppSelector } from '@hooks/redux-custom';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { isoStringToLegibleString } from '@helpers';

type Props = {
  tradingTimes: AvaSuburbHours | undefined;
  eta: any;
  suburb: string;
};

const TimePanel: FC<Props> = ({ tradingTimes, eta, suburb }) => {
  const theme = useTheme();
  const [isHoursModalOpen, setHoursModalOpen] = useState(false);
  const { deliveryTime } = useAppSelector((state) => state.order.value);

  const suburbMessageETA = `Estimated delivery time for ${suburb} is ${eta?.etaInMinutes} minutes`;

  /** Show the modal by default when the status is closed */

  const { openTime, closeTime } = useMemo(
    () =>
      tradingTimes && tradingTimes.ecom_trading_hours.length > 1
        ? {
            openTime: tradingTimes.ecom_trading_hours.find((item) => new Date().toLocaleString('en-us', { weekday: 'long' }) === item.day)
              ?.open,
            closeTime: tradingTimes.ecom_trading_hours.find((item) => new Date().toLocaleString('en-us', { weekday: 'long' }) === item.day)
              ?.closed,
          }
        : { openTime: '', closeTime: '' },
    [tradingTimes],
  );

  useEffect(() => {
    if (tradingTimes?.ecom_trading_status === 'closed') {
      setHoursModalOpen(true);
    }
  }, [tradingTimes]);

  if (!tradingTimes)
    return (
      <Grid
        item
        style={{
          padding: theme.spacing(2),
          margin: theme.spacing(2),
        }}
        data-testid="no-suburb-service-panel"
      >
        <Typography data-testid="no-service-text" variant="h6">
          The selected suburb has no valid AVA service
        </Typography>
      </Grid>
    );

  return (
    <>
      <TradingHoursModals
        isOpen={isHoursModalOpen}
        tradingDays={tradingTimes?.ecom_trading_hours ?? []}
        onClose={() => setHoursModalOpen(false)}
        isEcommClosed={tradingTimes.ecom_trading_status === 'closed'}
      />
      <Paper
        style={{
          padding: theme.spacing(2),
          margin: theme.spacing(2),
        }}
        data-testid="time-panel"
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography data-testid="opening-time">Opening time - {openTime}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography data-testid="closing-time">Close time - {closeTime}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => setHoursModalOpen(true)}
              data-testid="time-panel-show-timings-btn"
            >
              Show timings
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">{suburbMessageETA}</Alert>
          </Grid>
          <Grid item xs={12} display="flex" gap={1}>
            <ScheduleIcon />
            {deliveryTime?.type === 'ASAP' ? (
              <Typography data-testid="delivery-time">As soon as possible</Typography>
            ) : (
              <Box display="flex" flexDirection="column">
                <Typography fontWeight="bold" data-testid="future-delivery-time">
                  Scheduled:
                </Typography>
                <Typography data-testid="future-delivery-start-time">
                  {isoStringToLegibleString(deliveryTime.requested_delivery_time)} -
                </Typography>
                <Typography data-testid="future-delivery-end-time">
                  {isoStringToLegibleString(deliveryTime.requested_delivery_end_time)}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default TimePanel;
